import { defineComponent as _defineComponent } from 'vue'
import { resolveComponent as _resolveComponent, vShow as _vShow, createVNode as _createVNode, withDirectives as _withDirectives, withCtx as _withCtx, createElementVNode as _createElementVNode, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, toDisplayString as _toDisplayString, createCommentVNode as _createCommentVNode, createTextVNode as _createTextVNode, normalizeClass as _normalizeClass, withModifiers as _withModifiers, createBlock as _createBlock } from "vue"

const _hoisted_1 = { class: "page" }
const _hoisted_2 = { class: "section content-container" }
const _hoisted_3 = { class: "container is-fluid" }
const _hoisted_4 = { class: "columns" }
const _hoisted_5 = { class: "column" }
const _hoisted_6 = { class: "island" }
const _hoisted_7 = { class: "table is-fullwidth" }
const _hoisted_8 = { class: "icon-column" }
const _hoisted_9 = { class: "table-column--message" }
const _hoisted_10 = { class: "table-column--intent" }
const _hoisted_11 = { class: "field has-addons" }
const _hoisted_12 = { class: "control is-expanded" }
const _hoisted_13 = { class: "button" }
const _hoisted_14 = { class: "selected-intent-label" }
const _hoisted_15 = { class: "scrollable-item-list" }
const _hoisted_16 = ["onClick"]
const _hoisted_17 = {
  key: 0,
  class: "dropdown-divider"
}
const _hoisted_18 = ["onClick"]
const _hoisted_19 = { class: "add-item" }
const _hoisted_20 = { class: "add-intent" }
const _hoisted_21 = ["disabled"]
const _hoisted_22 = { class: "table-column--actions has-text-centered" }
const _hoisted_23 = ["onClick"]
const _hoisted_24 = ["onClick"]

import PagingControl, { Page } from '@/shared/components/PagingControl.vue';
import Toast from '@/shared/services/toast';
import Check from 'vue-material-design-icons/Check.vue';
import Close from 'vue-material-design-icons/Close.vue';
import EditIcon from 'vue-material-design-icons/SquareEditOutline.vue';
import ReloadIcon from 'vue-material-design-icons/Reload.vue';
import EditIntentModal from '@/features/bots/components/designer/EditIntentModal.vue';
import AddIntentModal from '@/features/bots/components/designer/AddIntentModal.vue';
import { nlpApi } from '@/features/bots/services/nlp';
import { PredictionViewModel } from '@/features/bots/models/intents/predictionViewModel';
import DropDownMenu from '@/shared/components/dropDownMenu/DropDownMenu.vue';
import { IntentScoreViewModel } from '@/features/bots/models/intents/intentScoreViewModel';
import AddIcon from 'vue-material-design-icons/Plus.vue';
import { IntentViewModel } from '@/features/bots/models/intents/intentViewModel';
import { useStore } from 'vuex';
import { computed, nextTick, onMounted, ref } from 'vue';


export default /*@__PURE__*/_defineComponent({
  __name: 'ActiveLearning',
  emits: ["modal-opened"],
  setup(__props, { emit: __emit }) {

const store = useStore();
const currentWorkspace = computed(() => store.getters.currentWorkspace);

const predictions = ref<PredictionViewModel[]>([]);
const addingUtterance = ref('');
const deletingPredictionId = ref('');
const loading = ref(true);
const total = ref(0);
const rowsPerPage = ref(25);
const startIndex = ref(0);
const intentToEditId = ref('');
const addModalVisible = ref(false);
const showEditModal = ref(false);

const addIntentModal = ref<typeof AddIntentModal | null>(null);

const emit = __emit;

onMounted(() => loadPredictionHistory());

function getFormattedIntentScore(intentScore: IntentScoreViewModel) {
  return intentScore.score === 0 ? `${intentScore.title} (N/A)` : `${intentScore.title} (${intentScore.score})`;
}

function onPageChanged(page: Page) {
  startIndex.value = page.start;
  loadPredictionHistory();
}

function editIntent(id: string) {
  if (isActionInProgress()) {
    return;
  }

  intentToEditId.value = id;
  nextTick(() => {
    showEditModal.value = true;
  });
}

async function addToIntent(utterance: string, intentId: string) {
  if (isActionInProgress()) {
    return;
  }

  try {
    addingUtterance.value = utterance;
    await nlpApi.addUtteranceToIntent(currentWorkspace.value.id, intentId, utterance);
    total.value--;
    predictions.value = predictions.value.filter((p: PredictionViewModel) => p.utterance !== utterance);
    Toast.showSuccess(`Added message to intent`);
  } catch (ex: any) {
    Toast.showError(`Failed to add message to intent: ${ex.message}`);
  } finally {
    addingUtterance.value = '';
  }
}

function isActionInProgress() {
  return addingUtterance.value !== '' || deletingPredictionId.value !== '';
}

function isUtteranceBeingAdded(utterance: string) {
  return addingUtterance.value === utterance;
}

function isPredictionBeingDeleted(utterance: string) {
  return deletingPredictionId.value === utterance;
}

async function reloadPredictions() {
  await loadPredictions();
  Toast.showSuccess(`Latest predictions loaded`);
}

function reloadPageMessage() {
  return 'Refresh page';
}

async function deletePrediction(predictionId: string) {
  if (isActionInProgress()) {
    return;
  }
  try {
    deletingPredictionId.value = predictionId;
    await nlpApi.deletePrediction(currentWorkspace.value.id, predictionId);
    total.value--;
    predictions.value = predictions.value.filter((p: PredictionViewModel) => p.id !== predictionId);
    Toast.showSuccess('Successfully removed message');
  } catch (err: any) {
    Toast.showError(`Failed to remove message: ${err.message}`);
  } finally {
    deletingPredictionId.value = '';
  }
}

async function addNewIntent() {
  addModalVisible.value = true;
  await nextTick(() => {
    if (addIntentModal.value) {
      addIntentModal.value.addNewIntent();
    }
  });
  emit('modal-opened');
}

function addedIntent(intent: IntentViewModel) {
  addModalVisible.value = false;
  showEditModal.value = false;
  const utterances = intent.utterances.map((u) => u.text.toLowerCase());
  const noPredictionsBefore = predictions.value.length;
  predictions.value = predictions.value.filter(
    (p: PredictionViewModel) => !utterances.includes(p.utterance.toLowerCase()),
  );
  total.value -= noPredictionsBefore - predictions.value.length;
}

async function loadPredictionHistory() {
  loading.value = true;
  await loadPredictions();
  loading.value = false;
}

async function loadPredictions() {
  try {
    const pagedResults = await nlpApi.getPredictions(currentWorkspace.value.id, rowsPerPage.value, startIndex.value);
    total.value = pagedResults.total;
    predictions.value = pagedResults.page;
    predictions.value.forEach((prediction: PredictionViewModel) => {
      prediction.selectedIntent = prediction.intentScores[0];
    });
  } catch (err: any) {
    Toast.showError('Failed to load predictions');
  }
}

return (_ctx: any,_cache: any) => {
  const _component_n_loading_spinner = _resolveComponent("n-loading-spinner")!
  const _component_n_tooltip = _resolveComponent("n-tooltip")!

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _withDirectives(_createVNode(_component_n_loading_spinner, null, null, 512), [
      [_vShow, loading.value]
    ]),
    _withDirectives(_createElementVNode("section", _hoisted_2, [
      _createElementVNode("div", _hoisted_3, [
        _createElementVNode("div", _hoisted_4, [
          _createElementVNode("div", _hoisted_5, [
            _createElementVNode("div", _hoisted_6, [
              _createElementVNode("table", _hoisted_7, [
                _createElementVNode("thead", null, [
                  _createElementVNode("tr", null, [
                    _createElementVNode("th", _hoisted_8, [
                      _createVNode(_component_n_tooltip, {
                        class: "reload-icon",
                        position: "bottom",
                        text: reloadPageMessage()
                      }, {
                        default: _withCtx(() => [
                          _createVNode(ReloadIcon, { onClick: reloadPredictions })
                        ]),
                        _: 1
                      }, 8, ["text"])
                    ]),
                    _cache[3] || (_cache[3] = _createElementVNode("th", null, "Message", -1)),
                    _cache[4] || (_cache[4] = _createElementVNode("th", null, "Intent", -1)),
                    _cache[5] || (_cache[5] = _createElementVNode("th", { class: "has-text-centered" }, "Add/Remove", -1))
                  ])
                ]),
                (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(predictions.value, (prediction) => {
                  return (_openBlock(), _createElementBlock("tr", {
                    key: prediction.utterance
                  }, [
                    _cache[7] || (_cache[7] = _createElementVNode("td", null, null, -1)),
                    _createElementVNode("td", _hoisted_9, _toDisplayString(prediction.utterance), 1),
                    _createElementVNode("td", _hoisted_10, [
                      _createElementVNode("div", _hoisted_11, [
                        _createElementVNode("div", _hoisted_12, [
                          _createElementVNode("div", {
                            class: _normalizeClass(["select is-fullwidth", { 'is-disabled': isActionInProgress() }])
                          }, [
                            _createVNode(DropDownMenu, {
                              "drop-down-icon": "none",
                              class: "intent-dropdown",
                              "go-left": false
                            }, {
                              trigger: _withCtx(() => [
                                _createElementVNode("button", _hoisted_13, [
                                  _createElementVNode("span", _hoisted_14, _toDisplayString(getFormattedIntentScore(prediction.selectedIntent!)), 1)
                                ])
                              ]),
                              default: _withCtx(() => [
                                _createElementVNode("div", _hoisted_15, [
                                  (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(prediction.intentScores, (intentScore) => {
                                    return (_openBlock(), _createElementBlock("a", {
                                      key: intentScore.id,
                                      class: "dropdown-item",
                                      onClick: ($event: any) => (prediction.selectedIntent = intentScore)
                                    }, _toDisplayString(getFormattedIntentScore(intentScore)), 9, _hoisted_16))
                                  }), 128))
                                ]),
                                (prediction.intentScores.length > 0)
                                  ? (_openBlock(), _createElementBlock("hr", _hoisted_17))
                                  : _createCommentVNode("", true),
                                _createElementVNode("a", {
                                  class: "dropdown-item",
                                  onClick: ($event: any) => (addNewIntent(prediction.utterance))
                                }, [
                                  _createElementVNode("div", _hoisted_19, [
                                    _createElementVNode("a", _hoisted_20, [
                                      _createVNode(AddIcon, { class: "icon-1-3x" }),
                                      _cache[6] || (_cache[6] = _createTextVNode(" Add new intent ", -1))
                                    ])
                                  ])
                                ], 8, _hoisted_18)
                              ]),
                              _: 2
                            }, 1024)
                          ], 2)
                        ]),
                        _createElementVNode("div", {
                          class: "control edit-icon",
                          disabled: isActionInProgress()
                        }, [
                          _createElementVNode("a", null, [
                            _createVNode(EditIcon, {
                              class: "icon-1-2x",
                              onClick: _withModifiers(($event: any) => (editIntent(prediction?.selectedIntent?.id || '')), ["stop"])
                            }, null, 8, ["onClick"])
                          ])
                        ], 8, _hoisted_21)
                      ])
                    ]),
                    _createElementVNode("td", _hoisted_22, [
                      _createElementVNode("button", {
                        class: _normalizeClass(["button is-success is-outlined", {
                        'is-loading': isUtteranceBeingAdded(prediction.utterance),
                        'is-disabled': isActionInProgress(),
                      }]),
                        onClick: ($event: any) => (addToIntent(prediction.utterance, prediction?.selectedIntent?.id || ''))
                      }, [
                        _createVNode(Check, { class: "icon-1-3x" })
                      ], 10, _hoisted_23),
                      _createElementVNode("button", {
                        class: _normalizeClass(["button is-danger is-outlined", {
                        'is-loading': isPredictionBeingDeleted(prediction.utterance),
                        'is-disabled': isActionInProgress(),
                      }]),
                        onClick: ($event: any) => (deletePrediction(prediction.id))
                      }, [
                        _createVNode(Close, { class: "icon-1-3x" })
                      ], 10, _hoisted_24)
                    ])
                  ]))
                }), 128))
              ]),
              _createVNode(PagingControl, {
                total: total.value,
                "rows-per-page": rowsPerPage.value,
                onPageChanged: onPageChanged,
                "disable-controls": isActionInProgress()
              }, null, 8, ["total", "rows-per-page", "disable-controls"])
            ])
          ])
        ])
      ])
    ], 512), [
      [_vShow, !loading.value]
    ]),
    (addModalVisible.value)
      ? (_openBlock(), _createBlock(AddIntentModal, {
          key: 0,
          ref_key: "addIntentModal",
          ref: addIntentModal,
          onCompleted: addedIntent,
          onClosed: _cache[0] || (_cache[0] = ($event: any) => (addModalVisible.value = false))
        }, null, 512))
      : _createCommentVNode("", true),
    (showEditModal.value)
      ? (_openBlock(), _createBlock(EditIntentModal, {
          key: 1,
          "intent-id": intentToEditId.value,
          onClosed: _cache[1] || (_cache[1] = ($event: any) => (showEditModal.value = false)),
          onCompleted: addedIntent,
          onFailedToLoad: _cache[2] || (_cache[2] = ($event: any) => (showEditModal.value = false))
        }, null, 8, ["intent-id"]))
      : _createCommentVNode("", true)
  ]))
}
}

})